import React, { useState, useEffect, useMemo } from 'react';
import guitarBuildOptions from '../assets/guitar_build_options_with_prices.json';

const GuitarBuilderPage = () => {
  // State management for 2-phase design
  const [selections, setSelections] = useState({});
  const [stepData] = useState(guitarBuildOptions);
  const [showResetWarning, setShowResetWarning] = useState(false);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [customerErrors, setCustomerErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
  
  // Separate the first 3 steps (buttons) from the rest (list)
  const primarySteps = stepData.slice(0, 3); // model, configuration, style
  const detailSteps = stepData.slice(3); // all other options

  // Initialize from URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const model = urlParams.get('model');
    const config = urlParams.get('config'); // ModelsGallery sends 'config'
    const configuration = urlParams.get('configuration'); // Support both formats
    const style = urlParams.get('style');

    if (model || config || configuration || style) {
      const initialSelections = {};
      if (model) {
        initialSelections.model = model.toLowerCase();
      }
      if (config) {
        initialSelections.configuration = config.toLowerCase(); // Map 'config' to 'configuration'
      }
      if (configuration) {
        initialSelections.configuration = configuration.toLowerCase();
      }
      if (style) {
        initialSelections.style = style.toLowerCase();
      }
      
      setSelections(initialSelections);
    }
  }, []);
  
  // Helper function to check if an option should be available (not disabled)
  const isOptionAvailable = (option, currentSelections) => {
    // If no disableIf condition, option is available
    if (!option.disableIf) return true;
    
    // Check if any disableIf condition is met (if so, disable the option)
    return !Object.entries(option.disableIf).some(([key, values]) => {
      return values.includes(currentSelections[key]);
    });
  };
  
  // Helper function to check if an option should be disabled (redundant now, but kept for clarity)
  const isOptionDisabled = (option, currentSelections) => {
    return !isOptionAvailable(option, currentSelections);
  };
  
  // Check if primary steps are complete
  const isPrimaryComplete = selections.model && selections.configuration && selections.style;
  
  // Helper function to check if a detailed step should be available based on previous selections
  const isDetailStepAvailable = (stepIndex) => {
    // Custom requests field is always available after primary steps are complete
    const currentStep = detailSteps[stepIndex];
    if (currentStep?.category === 'custom_requests' && isPrimaryComplete) {
      return true;
    }

    // Must complete primary steps first
    if (!isPrimaryComplete) return false;

    // For detailed steps, check if all previous detailed steps have selections OR are completely disabled
    for (let i = 0; i < stepIndex; i++) {
      const previousStep = detailSteps[i];
      const hasSelection = selections[previousStep.category];

      // Check if all options in this previous step are disabled
      const availableOptions = previousStep.options.filter(option =>
        isOptionAvailable(option, selections)
      );
      const allOptionsDisabled = availableOptions.length === 0;

      // Step is considered "complete" if it has a selection OR all options are disabled
      if (!hasSelection && !allOptionsDisabled) {
        return false;
      }
    }
    return true;
  };
  
  // Calculate base price and extras
  const { basePrice, totalExtras } = useMemo(() => {
    let base = 0;
    let extras = 0;
    
    // Get base price from configuration selection
    const configSelection = stepData.find(step => step.category === 'configuration');
    if (configSelection && selections.configuration) {
      const selectedOption = configSelection.options.find(opt => opt.id === selections.configuration);
      base = selectedOption?.basePrice || 0;
    }
    
    // Calculate extras from all other selections
    Object.entries(selections).forEach(([category, selectedId]) => {
      if (category === 'configuration') return; // Skip configuration as it's base price
      
      const step = stepData.find(s => s.category === category);
      if (step && step.options) { // Check if step has options array
        // Handle multi-choice selections (arrays)
        if (Array.isArray(selectedId)) {
          selectedId.forEach(id => {
            const option = step.options.find(opt => opt.id === id);
            if (option && option.extra) {
              extras += option.extra;
            }
          });
        } else {
          // Handle single selections
          const option = step.options.find(opt => opt.id === selectedId);
          if (option && option.extra) {
            extras += option.extra;
          }
        }
      }
      // Free text fields (step.freeText) don't have options, so skip them
    });
    
    return { basePrice: base, totalExtras: extras };
  }, [selections, stepData]);
  
  // Calculate total price
  const totalPrice = basePrice + totalExtras;
  
  // Handle selection for any category with cascade reset logic
  const handleSelection = (category, optionId) => {
    // Find the index of the changed category in the step order
    const changedStepIndex = stepData.findIndex(step => step.category === category);
    
    // Check if there are any selections after this step that would be reset
    const hasSelectionsAfter = stepData.some((step, index) => {
      return index > changedStepIndex && selections[step.category];
    });
    
    // Only show warning if:
    // 1. There are selections that would be reset, AND
    // 2. We're changing a step that affects detailed configuration (index >= 3)
    //    OR we're in primary steps but have detailed selections
    const isPrimaryStep = changedStepIndex < 3;
    const hasDetailedSelections = stepData.slice(3).some(step => selections[step.category]);
    
    const shouldShowWarning = hasSelectionsAfter && 
      (!isPrimaryStep || (isPrimaryStep && hasDetailedSelections));
    
    // If warning should be shown, show it
    if (shouldShowWarning) {
      setPendingSelection({ category, optionId, changedStepIndex });
      setShowResetWarning(true);
      return;
    }
    
    // Otherwise, proceed with selection
    applySelection(category, optionId, changedStepIndex);
  };
  
  // Apply the selection and reset dependent options
  const applySelection = (category, optionId, changedStepIndex) => {
    const newSelections = {
      ...selections,
      [category]: optionId
    };
    
    // Reset all selections that come after the changed step
    stepData.forEach((step, index) => {
      if (index > changedStepIndex) {
        delete newSelections[step.category];
      }
    });
    
    setSelections(newSelections);
  };
  
  // Confirm the reset and apply selection
  const confirmReset = () => {
    if (pendingSelection) {
      applySelection(pendingSelection.category, pendingSelection.optionId, pendingSelection.changedStepIndex);
    }
    setShowResetWarning(false);
    setPendingSelection(null);
  };
  
  // Cancel the reset
  const cancelReset = () => {
    setShowResetWarning(false);
    setPendingSelection(null);
  };
  
  // Check if form is complete (at least first 3 steps)
  const isFormComplete = isPrimaryComplete;

  const handleBackToHome = () => {
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
  
  const handleReset = () => {
    setSelections({});
  };

  const handleRequestQuote = () => {
    setShowQuoteModal(true);
    setSubmitStatus(null);
  };

  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
    if (customerErrors[name]) {
      setCustomerErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateCustomerInfo = () => {
    const errors = {};
    if (!customerInfo.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!customerInfo.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(customerInfo.email)) {
      errors.email = 'Email is invalid';
    }
    if (!customerInfo.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    setCustomerErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitQuote = async (e) => {
    e.preventDefault();

    if (!validateCustomerInfo()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    // Create formatted selections with display names for email
    const selectionsFormatted = {};
    Object.entries(selections).forEach(([category, value]) => {
      const step = stepData.find(s => s.category === category);
      if (Array.isArray(value)) {
        // Multi-choice: map IDs to names
        selectionsFormatted[category] = value
          .map(id => {
            const option = step?.options?.find(opt => opt.id === id);
            return option?.name || id;
          })
          .join(', ');
      } else if (step?.options) {
        // Single choice with options: find the option name
        const option = step.options.find(opt => opt.id === value);
        selectionsFormatted[category] = option?.name || value;
      } else {
        // Free text or no options (just display the value)
        selectionsFormatted[category] = value;
      }
    });

    try {
      const response = await fetch('https://vitaeguitars.com/api/email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'guitar-build',
          data: {
            customerInfo: {
              name: customerInfo.name,
              email: customerInfo.email,
              phone: customerInfo.phone,
              message: customerInfo.message
            },
            selections: selectionsFormatted,
            pricing: {
              basePrice,
              totalExtras,
              totalPrice
            },
            timestamp: new Date().toISOString()
          }
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
        console.error('Quote submission failed:', result);
      }
    } catch (error) {
      setSubmitStatus('error');
      console.error('Error submitting quote:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseQuoteModal = () => {
    setShowQuoteModal(false);
    if (submitStatus === 'success') {
      setCustomerInfo({ name: '', email: '', phone: '', message: '' });
      setSelections({});
      setSubmitStatus(null);
    }
  };

  return (
    <div className="min-h-screen bg-theme-bg-primary text-white font-sans">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-theme-bg-primary/90 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <button
              onClick={handleBackToHome}
              className="flex items-center gap-3 text-theme-text-primary hover:text-theme-primary-500 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="text-xl font-bold">Back to Vitæ Guitars</span>
            </button>
            
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24">
        <div className="relative py-20 overflow-hidden min-h-screen">
          {/* Animated background */}
          <div className="absolute inset-0 bg-gradient-to-b from-theme-bg-primary via-gray-900 to-black">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.03%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
          </div>

          <div className="container mx-auto px-6 relative z-10">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h2 className="text-5xl md:text-6xl font-bold pb-4 mb-4 bg-theme-primary-400 from-theme-primary-400 to-theme-accent-400 bg-clip-text text-transparent">
                Design Your Dream Guitar
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Configure every detail of your custom guitar. From wood selection to hardware choices, create the perfect instrument tailored to your style and sound.
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Left Panel - Configuration */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Phase 1: Primary Selection Buttons */}
                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                    <h3 className="text-2xl font-bold text-theme-primary-400 mb-6">Guitar Configuration</h3>
                    
                    <div className="grid md:grid-cols-3 gap-6">
                      {primarySteps.map((step) => (
                        <div key={step.category} className="space-y-3">
                          <h4 className="text-lg font-semibold text-white">{step.title}</h4>
                          <div className="space-y-2">
                            {step.options.map((option) => {
                              const isSelected = selections[step.category] === option.id;
                              
                              return (
                                <button
                                  key={option.id}
                                  onClick={() => handleSelection(step.category, option.id)}
                                  className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                                    isSelected
                                      ? 'border-theme-primary-500 bg-theme-primary-500/10 text-theme-primary-400'
                                      : 'border-white/10 bg-gray-800/50 text-white hover:border-theme-primary-500/50 hover:bg-theme-primary-500/5'
                                  }`}
                                >
                                  <div className="font-semibold">{option.name}</div>
                                  {step.category === 'style' && option.desc && (
                                    <div className="text-sm text-gray-300 mt-1">
                                      {option.desc}
                                    </div>
                                  )}
                                  {step.category === 'configuration' && option.desc && (
                                    <div className="text-sm text-gray-300 mt-1">
                                      {option.desc}
                                    </div>
                                  )}
                                  {step.category === 'configuration' && option.basePrice && (
                                    <div className="text-sm text-theme-primary-400 font-semibold mt-1">
                                      Base: ${option.basePrice}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <>
                    {/* Phase 2: Detailed Options List */}
                    {isPrimaryComplete && (
                      <div className="bg-gray-900/50 rounded-xl p-6 border border-white/10">
                        <h3 className="text-2xl font-bold text-theme-primary-400 mb-6">Detailed Configuration</h3>
                        
                        <div className="space-y-6">
                          {detailSteps.map((step, stepIndex) => {
                            const stepAvailable = isDetailStepAvailable(stepIndex);
                            const selectedValue = selections[step.category] || '';
                            
                            // Check if all options in this category are disabled
                            const availableOptions = step.options ? step.options.filter(option => 
                              isOptionAvailable(option, selections)
                            ) : [];
                            const allOptionsDisabled = stepAvailable && !step.freeText && availableOptions.length === 0;
                            const categoryDisabled = !stepAvailable || allOptionsDisabled;
                            
                            return (
                              <div key={step.category} className="pb-4">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4">
                                  <div className="flex items-center gap-3">
                                    <h4 className={`text-base font-semibold ${
                                      categoryDisabled ? 'text-gray-500' : 'text-white'
                                    }`}>{step.title}</h4>
                                  </div>

                                  <div className="w-full md:flex-1 md:max-w-md">
                                    {allOptionsDisabled ? (
                                      // When no options available, show badge in place of dropdown
                                      <div className="flex justify-end">
                                        <span className="text-xs bg-gray-700 text-gray-400 px-2 py-1 rounded">
                                          No options available for current configuration
                                        </span>
                                      </div>
                                    ) : step.freeText ? (
                                      // Free text input
                                      <div className="relative">
                                        <textarea
                                          value={selections[step.category] || ''}
                                          onChange={(e) => !categoryDisabled && handleSelection(step.category, e.target.value)}
                                          disabled={categoryDisabled}
                                          placeholder={step.placeholder || `Enter ${step.title.toLowerCase()}...`}
                                          rows={3}
                                          className={`w-full p-3 rounded border bg-gray-800 text-white text-sm resize-none ${
                                            categoryDisabled
                                              ? 'border-gray-700 bg-gray-800/30 text-gray-500 cursor-not-allowed opacity-50'
                                              : selections[step.category]
                                              ? 'border-theme-primary-500 bg-theme-primary-500/10 text-theme-primary-400'
                                              : 'border-white/10 hover:border-theme-primary-500/50 focus:border-theme-primary-500 focus:outline-none'
                                          }`}
                                        />
                                      </div>
                                    ) : step.multiChoice ? (
                                      // Multi-choice selection - grid on desktop, checkboxes on mobile
                                      <div className="space-y-2">
                                        {/* Selection limit warning for multi-choice with limits */}
                                        {step.selectionLimits && (
                                          <div className="text-xs text-gray-400 mb-2 text-right">
                                            {`Select up to ${step.selectionLimits[selections.configuration] || 'unlimited'} pieces`}
                                          </div>
                                        )}
                                        {/* Desktop: Grid boxes */}
                                        <div className="hidden md:grid md:grid-cols-2 gap-2">
                                          {!allOptionsDisabled && step.options && step.options
                                            .filter(option => isOptionAvailable(option, selections))
                                            .map((option) => {
                                              const isSelected = Array.isArray(selections[step.category])
                                                ? selections[step.category].includes(option.id)
                                                : false;

                                              // Check selection limits from JSON configuration
                                              const currentSelections = Array.isArray(selections[step.category])
                                                ? selections[step.category]
                                                : [];
                                              const maxSelections = step.selectionLimits
                                                ? (step.selectionLimits[selections.configuration] || Infinity)
                                                : Infinity;
                                              const canSelect = isSelected || currentSelections.length < maxSelections;

                                              return (
                                                <button
                                                  key={option.id}
                                                  type="button"
                                                  onClick={() => {
                                                    if (!categoryDisabled && canSelect) {
                                                      let newSelections;
                                                      if (isSelected) {
                                                        newSelections = currentSelections.filter(id => id !== option.id);
                                                      } else {
                                                        newSelections = [...currentSelections, option.id];
                                                      }

                                                      handleSelection(step.category, newSelections);
                                                    }
                                                  }}
                                                  disabled={categoryDisabled || !canSelect}
                                                  className={`p-3 rounded-lg border-2 transition-all text-left text-sm ${
                                                    categoryDisabled || !canSelect
                                                      ? 'border-gray-700 bg-gray-800/30 text-gray-500 cursor-not-allowed opacity-50'
                                                      : isSelected
                                                      ? 'border-theme-primary-500 bg-theme-primary-500/10 text-theme-primary-400'
                                                      : 'border-white/10 bg-gray-800/50 text-white hover:border-theme-primary-500/50 hover:bg-theme-primary-500/5'
                                                  }`}
                                                >
                                                  <div className="flex items-center justify-between gap-2">
                                                    <span className="font-medium">{option.name}</span>
                                                    {isSelected && (
                                                      <i className="fas fa-check-circle text-theme-primary-400"></i>
                                                    )}
                                                  </div>
                                                  {option.extra !== undefined && option.extra > 0 && (
                                                    <div className="text-xs text-theme-primary-400 mt-1">
                                                      +${option.extra}
                                                    </div>
                                                  )}
                                                </button>
                                              );
                                            })}
                                        </div>
                                        {/* Mobile: Checkboxes */}
                                        <div className="md:hidden space-y-2">
                                          {!allOptionsDisabled && step.options && step.options
                                            .filter(option => isOptionAvailable(option, selections))
                                            .map((option) => {
                                              const isSelected = Array.isArray(selections[step.category])
                                                ? selections[step.category].includes(option.id)
                                                : false;

                                              // Check selection limits from JSON configuration
                                              const currentSelections = Array.isArray(selections[step.category])
                                                ? selections[step.category]
                                                : [];
                                              const maxSelections = step.selectionLimits
                                                ? (step.selectionLimits[selections.configuration] || Infinity)
                                                : Infinity;
                                              const canSelect = isSelected || currentSelections.length < maxSelections;

                                              return (
                                                <label
                                                  key={option.id}
                                                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                                                    categoryDisabled || !canSelect
                                                      ? 'border-gray-700 bg-gray-800/30 text-gray-500 cursor-not-allowed opacity-50'
                                                      : isSelected
                                                      ? 'border-theme-primary-500 bg-theme-primary-500/10'
                                                      : 'border-white/10 bg-gray-800/50 hover:border-theme-primary-500/50'
                                                  }`}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    disabled={categoryDisabled || !canSelect}
                                                    onChange={() => {
                                                      if (!categoryDisabled && canSelect) {
                                                        let newSelections;
                                                        if (isSelected) {
                                                          newSelections = currentSelections.filter(id => id !== option.id);
                                                        } else {
                                                          newSelections = [...currentSelections, option.id];
                                                        }

                                                        handleSelection(step.category, newSelections);
                                                      }
                                                    }}
                                                    className="w-4 h-4 rounded border-gray-300 text-theme-primary-500 focus:ring-theme-primary-500"
                                                  />
                                                  <div className="flex-1">
                                                    <span className={`text-sm ${isSelected ? 'text-theme-primary-400 font-medium' : 'text-white'}`}>
                                                      {option.name}
                                                    </span>
                                                    {option.extra !== undefined && option.extra > 0 && (
                                                      <span className="text-xs text-theme-primary-400 ml-2">
                                                        +${option.extra}
                                                      </span>
                                                    )}
                                                  </div>
                                                </label>
                                              );
                                            })}
                                        </div>
                                      </div>
                                    ) : (
                                      // Single-choice dropdown - compact
                                      <div className="relative">
                                        <select
                                          value={selectedValue}
                                          onChange={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (!categoryDisabled) {
                                              handleSelection(step.category, e.target.value);
                                            }
                                          }}
                                          onFocus={(e) => {
                                            e.stopPropagation();
                                          }}
                                          onBlur={(e) => {
                                            e.stopPropagation();
                                          }}
                                          onTouchEnd={(e) => {
                                            e.stopPropagation();
                                          }}
                                          disabled={categoryDisabled}
                                          style={{
                                            WebkitTapHighlightColor: 'transparent',
                                            WebkitAppearance: 'none',
                                            MozAppearance: 'none',
                                            touchAction: 'manipulation'
                                          }}
                                          className={`w-full p-2 rounded border bg-gray-800 text-white text-sm appearance-none cursor-pointer transition-none [-webkit-tap-highlight-color:transparent] [touch-action:manipulation] ${
                                            categoryDisabled
                                              ? 'border-gray-700 bg-gray-800/30 text-gray-500 cursor-not-allowed opacity-50'
                                              : selectedValue
                                              ? 'border-theme-primary-500 bg-theme-primary-500/10 text-theme-primary-400'
                                              : 'border-white/10 md:hover:border-theme-primary-500/50 focus:border-theme-primary-500 focus:outline-none'
                                          }`}
                                        >
                                          <option value="" className="bg-gray-800 text-gray-400">
                                            {allOptionsDisabled ? 'No options available' : `Select ${step.title}`}
                                          </option>
                                          {!allOptionsDisabled && step.options && step.options
                                            .filter(option => isOptionAvailable(option, selections))
                                            .map((option) => {
                                              return (
                                                <option
                                                  key={option.id}
                                                  value={option.id}
                                                  className="bg-gray-800 text-white hover:bg-gray-700"
                                                >
                                                  {option.name}
                                                  {option.extra !== undefined && (
                                                    ` ${option.extra > 0 ? `(+$${option.extra})` : ''}`
                                                  )}
                                                </option>
                                              );
                                            })}
                                        </select>
                                      
                                        {/* Custom dropdown arrow */}
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                          <i className={`fas fa-chevron-down text-xs ${
                                            !stepAvailable ? 'text-gray-500' : 'text-gray-400'
                                          }`}></i>
                                        </div>
                                        
                                        {/* Show description for selected option */}
                                        {selectedValue && step.options && (
                                          (() => {
                                            const selectedOption = step.options.find(opt => opt.id === selectedValue);
                                            return selectedOption?.desc && (
                                              <div className="mt-2 text-xs text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded p-2">
                                                <i className="fas fa-info-circle mr-1"></i>
                                                {selectedOption.desc}
                                              </div>
                                            );
                                          })()
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Request Quote button at bottom of selections */}
                        <div className="text-center mt-6">
                          <button
                            onClick={handleRequestQuote}
                            disabled={!isFormComplete}
                            className={`py-3 px-8 rounded-lg font-semibold transition-all ${
                              isFormComplete
                                ? 'bg-theme-primary-500 text-white hover:shadow-lg hover:shadow-theme-primary-500/25 transform hover:-translate-y-0.5'
                                : 'bg-gray-700 text-gray-400 cursor-not-allowed opacity-50'
                            }`}
                          >
                            Request Quote
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Prompt to complete primary selection */}
                    {!isPrimaryComplete && (
                      <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-white/10 text-center">
                        <i className="fas fa-info-circle text-3xl text-theme-primary-500 mb-4"></i>
                        <h3 className="text-xl font-bold text-theme-primary-400 mb-2">
                          Complete Basic Configuration
                        </h3>
                        <p className="text-gray-300">
                          Please select a model, configuration, and style to access detailed customization options.
                        </p>
                      </div>
                    )}
                  </>
                </div>

                {/* Right Panel - Summary & Price */}
                <div className="lg:col-span-1">
                  <div className="lg:sticky lg:top-32">
                    {/* Price Display */}
                    <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-white/10 mb-6">
                      <div className="text-center mb-4">
                        <p className="text-sm text-gray-400 uppercase tracking-wider mb-2">Total Price</p>
                        <p className="text-5xl font-bold text-theme-primary-400">
                          ${totalPrice.toLocaleString()}
                        </p>
                        {basePrice > 0 && (
                          <p className="text-sm text-gray-400 mt-2">
                            Base: ${basePrice.toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Configuration Summary */}
                    <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                      <h4 className="text-lg font-bold mb-4 text-theme-primary-400">Your Configuration</h4>
                      
                      <div className="space-y-3 text-sm">
                        {/* Current Selections */}
                        <div className="pb-3 border-b border-white/10">
                          {stepData.map((step) => {
                            const selection = selections[step.category];
                            const selectedOption = selection && step.options ? step.options.find(opt => opt.id === selection) : null;
                            
                            return (
                              <div key={step.category} className="flex justify-between items-center mb-2">
                                <span className="text-gray-400">{step.title}:</span>
                                <span className="text-white font-medium">
                                  {selectedOption ? selectedOption.name : 'Not selected'}
                                </span>
                              </div>
                            );
                          })}
                        </div>

                        {/* Selected Options with Extras */}
                        {Object.entries(selections).length > 0 && (
                          <div className="space-y-2">
                            {Object.entries(selections).map(([category, selectedId]) => {
                              const step = stepData.find(s => s.category === category);
                              const selectedOption = step?.options ? step.options.find(opt => opt.id === selectedId) : null;
                              const extraCost = selectedOption?.extra || 0;
                              
                              if (!extraCost || extraCost === 0) return null;
                              
                              return (
                                <div key={category} className="flex justify-between items-center">
                                  <span className="text-gray-400">{step?.title}:</span>
                                  <div className="text-right">
                                    <div className="text-white font-medium">{selectedOption?.name}</div>
                                    <div className="text-theme-primary-400 text-xs">+${extraCost}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-6 space-y-3">
                        <button
                          onClick={handleRequestQuote}
                          disabled={!isFormComplete}
                          className={`w-full py-3 px-6 rounded-lg font-semibold transition-all ${
                            isFormComplete
                              ? 'bg-theme-primary-500 text-white hover:shadow-lg hover:shadow-theme-primary-500/25 transform hover:-translate-y-0.5'
                              : 'bg-gray-700 text-gray-400 cursor-not-allowed opacity-50'
                          }`}
                        >
                          Request Quote
                        </button>
                        
                        <button
                          onClick={handleReset}
                          className="w-full py-3 px-6 rounded-lg font-semibold border border-white/10 text-gray-300 hover:bg-white/5 transition-colors"
                        >
                          Reset Configuration
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Reset Warning Modal */}
      {showResetWarning && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-white/10 rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <i className="fas fa-exclamation-triangle text-2xl text-yellow-500"></i>
              <h3 className="text-xl font-bold text-white">Reset Dependent Selections?</h3>
            </div>

            <p className="text-gray-300 mb-6">
              Changing this option will reset all your selections that come after it.
              You'll need to reconfigure those options again.
            </p>

            <div className="flex gap-3">
              <button
                onClick={confirmReset}
                className="flex-1 bg-theme-primary-500 hover:bg-theme-primary-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                Continue & Reset
              </button>
              <button
                onClick={cancelReset}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quote Request Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-gray-900 border border-white/10 rounded-xl p-6 max-w-2xl w-full my-8">
            {submitStatus === 'success' ? (
              /* Success View - Thank You Message with Summary */
              <>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-4">
                    <i className="fas fa-check-circle text-4xl text-green-400"></i>
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-2">Thank You!</h3>
                  <p className="text-gray-300">
                    Your quote request has been sent successfully. We'll get back to you soon.
                  </p>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-2 text-theme-primary-400">Your Configuration</h4>
                  <div className="bg-gray-800/50 rounded-lg p-4 space-y-2 text-sm max-h-48 overflow-y-auto">
                    {Object.entries(selections).map(([category, value]) => {
                      const step = stepData.find(s => s.category === category);

                      // Format the value to show option names instead of IDs
                      let formattedValue;
                      if (Array.isArray(value)) {
                        // Multi-choice: map IDs to names
                        formattedValue = value
                          .map(id => {
                            const option = step?.options?.find(opt => opt.id === id);
                            return option?.name || id;
                          })
                          .join(', ');
                      } else if (step?.options) {
                        // Single choice with options: find the option name
                        const option = step.options.find(opt => opt.id === value);
                        formattedValue = option?.name || value;
                      } else {
                        // Free text or no options (just display the value)
                        formattedValue = value;
                      }

                      return (
                        <div key={category} className="flex justify-between gap-4">
                          <span className="text-gray-400 flex-shrink-0">{step?.title}:</span>
                          <span className="text-white font-medium text-right">{formattedValue}</span>
                        </div>
                      );
                    })}
                    <div className="pt-2 border-t border-white/10 flex justify-between font-bold">
                      <span className="text-theme-primary-400">Total Price:</span>
                      <span className="text-theme-primary-400">${totalPrice.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCloseQuoteModal}
                  className="w-full bg-theme-primary-500 hover:bg-theme-primary-600 text-white px-4 py-3 rounded-lg font-semibold transition-colors"
                >
                  Close
                </button>
              </>
            ) : (
              /* Form View - Request Quote Form */
              <>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-white">Request Your Quote</h3>
                  <button
                    onClick={handleCloseQuoteModal}
                    disabled={isSubmitting}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <i className="fas fa-times text-xl"></i>
                  </button>
                </div>

                {/* Error Message */}
                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg">
                    <p className="text-red-400 flex items-center">
                      <i className="fas fa-exclamation-circle mr-2"></i>
                      Something went wrong. Please try again or contact us directly.
                    </p>
                  </div>
                )}

            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-2 text-theme-primary-400">Your Configuration</h4>
              <div className="bg-gray-800/50 rounded-lg p-4 space-y-2 text-sm max-h-48 overflow-y-auto">
                {Object.entries(selections).map(([category, value]) => {
                  const step = stepData.find(s => s.category === category);

                  // Format the value to show option names instead of IDs
                  let formattedValue;
                  if (Array.isArray(value)) {
                    // Multi-choice: map IDs to names
                    formattedValue = value
                      .map(id => {
                        const option = step?.options?.find(opt => opt.id === id);
                        return option?.name || id;
                      })
                      .join(', ');
                  } else if (step?.options) {
                    // Single choice with options: find the option name
                    const option = step.options.find(opt => opt.id === value);
                    formattedValue = option?.name || value;
                  } else {
                    // Free text or no options (just display the value)
                    formattedValue = value;
                  }

                  return (
                    <div key={category} className="flex justify-between gap-4">
                      <span className="text-gray-400 flex-shrink-0">{step?.title}:</span>
                      <span className="text-white font-medium text-right">{formattedValue}</span>
                    </div>
                  );
                })}
                <div className="pt-2 border-t border-white/10 flex justify-between font-bold">
                  <span className="text-theme-primary-400">Total Price:</span>
                  <span className="text-theme-primary-400">${totalPrice.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmitQuote} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={customerInfo.name}
                  onChange={handleCustomerInfoChange}
                  disabled={isSubmitting}
                  className={`w-full px-4 py-2 bg-gray-800 border ${customerErrors.name ? 'border-red-500' : 'border-white/10'} rounded-lg focus:outline-none focus:ring-2 focus:ring-theme-primary-500 text-white disabled:opacity-50`}
                  placeholder="Your name"
                />
                {customerErrors.name && <p className="text-red-500 text-sm mt-1">{customerErrors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={customerInfo.email}
                  onChange={handleCustomerInfoChange}
                  disabled={isSubmitting}
                  className={`w-full px-4 py-2 bg-gray-800 border ${customerErrors.email ? 'border-red-500' : 'border-white/10'} rounded-lg focus:outline-none focus:ring-2 focus:ring-theme-primary-500 text-white disabled:opacity-50`}
                  placeholder="your.email@example.com"
                />
                {customerErrors.email && <p className="text-red-500 text-sm mt-1">{customerErrors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={customerInfo.phone}
                  onChange={handleCustomerInfoChange}
                  disabled={isSubmitting}
                  className={`w-full px-4 py-2 bg-gray-800 border ${customerErrors.phone ? 'border-red-500' : 'border-white/10'} rounded-lg focus:outline-none focus:ring-2 focus:ring-theme-primary-500 text-white disabled:opacity-50`}
                  placeholder="+1 234 567 8900"
                />
                {customerErrors.phone && <p className="text-red-500 text-sm mt-1">{customerErrors.phone}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">
                  Additional Notes (Optional)
                </label>
                <textarea
                  name="message"
                  value={customerInfo.message}
                  onChange={handleCustomerInfoChange}
                  disabled={isSubmitting}
                  rows={4}
                  className="w-full px-4 py-2 bg-gray-800 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme-primary-500 text-white disabled:opacity-50"
                  placeholder="Any special requests or questions?"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseQuoteModal}
                  disabled={isSubmitting}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-4 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-theme-primary-500 hover:bg-theme-primary-600 text-white px-4 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Sending...
                    </>
                  ) : (
                    'Submit Quote Request'
                  )}
                </button>
              </div>
            </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuitarBuilderPage;
